// /frontend/dashboard/src/services/apiClient.js
// Core HTTP client for dashboard service facades.
// English explanations are followed by Thai explanations for architecture validation and debugging.

import { DASHBOARD_DEBUG } from '../utils/debugGate.js';

const DEBUG_NAMESPACE = '[services/apiClient]';
const DEFAULT_TIMEOUT_MS = 30000;
const DEFAULT_RETRY_COUNT = 0;

function getTimerApi() {
  const timerHost = typeof window !== 'undefined' ? window : globalThis;
  return {
    setTimeout: timerHost.setTimeout.bind(timerHost),
    clearTimeout: timerHost.clearTimeout.bind(timerHost),
  };
}

function toQueryString(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === null || item === undefined || item === '') return;
        searchParams.append(key, String(item));
      });
      return;
    }

    if (typeof value === 'object') {
      searchParams.append(key, JSON.stringify(value));
      return;
    }

    searchParams.append(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function joinUrl(baseUrl = '', path = '') {
  const normalizedBase = String(baseUrl || '').replace(/\/$/, '');
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;

  if (!normalizedBase) {
    return normalizedPath;
  }

  return `${normalizedBase}${normalizedPath}`;
}

function createLogger(enabled = true) {
  function print(method, label, payload) {
    if (!enabled) return;
    const message = `${DEBUG_NAMESPACE} ${label}`;
    if (payload === undefined) {
      console[method](message);
      return;
    }
    console[method](message, payload);
  }

  return {
    info(label, payload) {
      print('log', label, payload);
    },
    warn(label, payload) {
      print('warn', label, payload);
    },
    error(label, payload) {
      print('error', label, payload);
    },
  };
}

function createTimeoutController(timeoutMs) {
  if (!timeoutMs || timeoutMs <= 0) {
    return {
      controller: new AbortController(),
      cleanup() {},
    };
  }

  const controller = new AbortController();
  const { setTimeout, clearTimeout } = getTimerApi();
  const timer = setTimeout(() => {
    controller.abort(new Error(`Request timeout after ${timeoutMs} ms`));
  }, timeoutMs);

  return {
    controller,
    cleanup() {
      clearTimeout(timer);
    },
  };
}

function normalizeHeaders(headers = {}) {
  const next = {};
  Object.entries(headers || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    next[String(key)] = String(value);
  });
  return next;
}

function normalizeError(error, context = {}) {
  const message = error instanceof Error ? error.message : String(error || 'Unknown request error');
  return {
    ok: false,
    name: error instanceof Error ? error.name : 'RequestError',
    message,
    context,
    explanation_en: 'Normalized HTTP error from apiClient. Use this to verify whether request failures are centralized in the shared client instead of being handled differently in each service facade.',
    explanation_th: 'error HTTP ที่ถูก normalize จาก apiClient ใช้ค่านี้ตรวจว่าความล้มเหลวของ request ถูกจัดการรวมศูนย์ใน shared client แทนที่จะกระจัดกระจายในแต่ละ service facade',
  };
}

function tryParseJson(text) {
  if (!text || !String(text).trim()) return null;
  try {
    return JSON.parse(text);
  } catch (_error) {
    return null;
  }
}

function buildRequestSummary({ method, url, headers, body, timeoutMs, retryCount, attempt }) {
  return {
    method,
    url,
    headers,
    hasBody: body !== undefined && body !== null,
    timeoutMs,
    retryCount,
    attempt,
    explanation_en: 'Outgoing request summary from apiClient. Compare this against service facade expectations to verify that robotApi, missionApi, and alertApi share one common HTTP transport layer.',
    explanation_th: 'สรุป request ขาออกจาก apiClient ใช้ค่านี้เทียบกับความคาดหวังของ service facade เพื่อยืนยันว่า robotApi, missionApi และ alertApi ใช้ HTTP transport layer กลางร่วมกัน',
  };
}

function buildResponseSummary({ method, url, status, ok, durationMs, rawText, parsed, attempt }) {
  return {
    method,
    url,
    status,
    ok,
    durationMs,
    attempt,
    hasParsedJson: Boolean(parsed && typeof parsed === 'object'),
    rawTextPreview: typeof rawText === 'string' ? rawText.slice(0, 500) : null,
    explanation_en: 'Incoming response summary from apiClient. Compare this against backend API contracts and facade-level normalization logic.',
    explanation_th: 'สรุป response ขาเข้าจาก apiClient ใช้ค่านี้เทียบกับสัญญา API ของ backend และ logic การ normalize ระดับ facade',
  };
}

class ApiClient {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || '';
    this.defaultHeaders = normalizeHeaders(options.defaultHeaders || {
      Accept: 'application/json',
    });
    this.timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : DEFAULT_TIMEOUT_MS;
    this.retryCount = typeof options.retryCount === 'number' ? options.retryCount : DEFAULT_RETRY_COUNT;
    this.debug = options.debug !== false;
    this.logger = createLogger(this.debug);
    this.lastRequestMeta = null;
    this.lastResponseMeta = null;
    this.requestCount = 0;
    this.errorCount = 0;

    this.logger.info(
      '[API_CLIENT_READY] English: apiClient core HTTP layer created | ไทย: สร้าง core HTTP layer ของ apiClient แล้ว',
      {
        baseUrl: this.baseUrl,
        timeoutMs: this.timeoutMs,
        retryCount: this.retryCount,
        defaultHeaders: this.defaultHeaders,
        explanation_en: 'Use this snapshot to verify that apiClient.js now acts as the shared HTTP core layer for robotApi, missionApi, and alertApi.',
        explanation_th: 'ใช้ snapshot นี้ตรวจว่า apiClient.js ตอนนี้ทำหน้าที่เป็น shared HTTP core layer สำหรับ robotApi, missionApi และ alertApi แล้ว',
      },
    );
  }

  setBaseUrl(baseUrl = '') {
    this.baseUrl = String(baseUrl || '');
    this.logger.info(
      '[CONFIG][BASE_URL_UPDATED] English: apiClient base URL updated | ไทย: อัปเดต base URL ของ apiClient แล้ว',
      {
        baseUrl: this.baseUrl,
      },
    );
  }

  setDefaultHeader(key, value) {
    if (!key) return;
    if (value === null || value === undefined || value === '') {
      delete this.defaultHeaders[key];
    } else {
      this.defaultHeaders[key] = String(value);
    }

    this.logger.info(
      '[CONFIG][DEFAULT_HEADER_UPDATED] English: apiClient default header updated | ไทย: อัปเดต default header ของ apiClient แล้ว',
      {
        key,
        hasValue: value !== null && value !== undefined && value !== '',
        defaultHeaders: { ...this.defaultHeaders },
      },
    );
  }

  setAuthToken(token) {
    if (!token) {
      delete this.defaultHeaders.Authorization;
    } else {
      this.defaultHeaders.Authorization = `Bearer ${String(token).trim()}`;
    }

    this.logger.info(
      '[CONFIG][AUTH_UPDATED] English: apiClient auth header updated | ไทย: อัปเดต auth header ของ apiClient แล้ว',
      {
        hasAuthorization: Boolean(this.defaultHeaders.Authorization),
        explanation_en: 'Use this to verify whether service facades will share one centralized authorization header policy.',
        explanation_th: 'ใช้ค่านี้ตรวจว่า service facades จะใช้ policy ของ authorization header แบบรวมศูนย์ร่วมกันหรือไม่',
      },
    );
  }

  async request(method, path, options = {}) {
    const upperMethod = String(method || 'GET').toUpperCase();
    const query = toQueryString(options.params || {});
    const url = `${joinUrl(this.baseUrl, path)}${query}`;
    const timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : this.timeoutMs;
    const retryCount = typeof options.retryCount === 'number' ? options.retryCount : this.retryCount;
    const headers = normalizeHeaders({
      ...this.defaultHeaders,
      ...(options.headers || {}),
    });

    const hasJsonBody = options.body !== undefined && options.body !== null && upperMethod !== 'GET';
    if (hasJsonBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (typeof options.body === 'string' && headers['Content-Type'] === 'application/json') {
      // English: keep caller-supplied JSON string body as-is; do not re-stringify.
      // ไทย: ถ้า caller ส่ง JSON string มาเอง ให้ใช้ตามเดิมโดยไม่ stringify ซ้ำ
    }

    let lastError = null;

    for (let attempt = 0; attempt <= retryCount; attempt += 1) {
      const startedAt = Date.now();
      const { controller, cleanup } = createTimeoutController(timeoutMs);
      const body = hasJsonBody
        ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body))
        : undefined;

      const requestSummary = buildRequestSummary({
        method: upperMethod,
        url,
        headers,
        body: options.body,
        timeoutMs,
        retryCount,
        attempt,
      });

      this.lastRequestMeta = requestSummary;
      this.requestCount += 1;

      this.logger.info(
        '[REQUEST] English: apiClient request started | ไทย: เริ่ม request ผ่าน apiClient แล้ว',
        requestSummary,
      );

      try {
        const response = await fetch(url, {
          method: upperMethod,
          headers,
          body,
          signal: controller.signal,
          credentials: options.credentials ?? 'include',
        });

        const rawText = await response.text();
        const parsed = tryParseJson(rawText);
        const durationMs = Date.now() - startedAt;

        const responseSummary = buildResponseSummary({
          method: upperMethod,
          url,
          status: response.status,
          ok: response.ok,
          durationMs,
          rawText,
          parsed,
          attempt,
        });

        this.lastResponseMeta = responseSummary;

        this.logger.info(
          '[RESPONSE] English: apiClient response received | ไทย: ได้รับ response จาก apiClient แล้ว',
          responseSummary,
        );

        cleanup();

        if (!response.ok) {
          // English: Prefer a backend-supplied human message over the generic URL string.
          //          Check parsed.message → parsed.error → trimmed rawText (≤ 200 chars) in order.
          //          Fall back to the HTTP status line only if no body message is available.
          //          This surfaces "Email already in use" / "Invalid token" to callers directly
          //          instead of burying it in normalized.context.parsed where callers never look.
          // ไทย: ใช้ข้อความจาก backend แทน URL string ทั่วไป ตรวจตามลำดับ:
          //      parsed.message → parsed.error → rawText ที่ trim แล้ว (≤ 200 ตัวอักษร)
          //      ถ้าไม่มีข้อความจาก body ค่อย fallback เป็น HTTP status line
          const backendMessage =
            // case: { message: "..." }
            (parsed && typeof parsed.message === 'string' && parsed.message.trim()) ||

            // ✅ case: { error: { message: "..." } }
            (parsed && parsed.error && typeof parsed.error.message === 'string' && parsed.error.message.trim()) ||

            // case: { error: "..." }
            (parsed && typeof parsed.error === 'string' && parsed.error.trim()) ||

            // fallback (short raw text only)
            (typeof rawText === 'string' &&
              rawText.trim().length > 0 &&
              rawText.trim().length <= 200 &&
              rawText.trim()) ||

            null;
          const httpError = new Error(
            backendMessage || `HTTP ${response.status} for ${upperMethod} ${url}`,
          );
          const normalized = normalizeError(httpError, {
            method: upperMethod,
            url,
            status: response.status,
            parsed,
            rawText,
            attempt,
          });

          this.logger.error(
            '[RESPONSE][HTTP_ERROR] English: apiClient received non-OK HTTP status | ไทย: apiClient ได้รับ HTTP status ที่ไม่สำเร็จ',
            normalized,
          );

          if (attempt < retryCount) {
            this.logger.warn(
              '[RETRY] English: retrying failed HTTP request | ไทย: กำลัง retry request ที่ล้มเหลว',
              {
                attempt,
                retryCount,
                url,
              },
            );
            continue;
          }

          throw normalized;
        }

        return parsed !== null ? parsed : { ok: true, rawText };
      } catch (error) {
        cleanup();
        const normalized = error && error.ok === false
          ? error
          : normalizeError(error, {
              method: upperMethod,
              url,
              attempt,
            });

        this.errorCount += 1;
        lastError = normalized;

        this.logger.error(
          '[REQUEST][FAILED] English: apiClient request failed | ไทย: request ของ apiClient ล้มเหลว',
          normalized,
        );

        if (attempt < retryCount) {
          this.logger.warn(
            '[RETRY] English: retrying failed request after transport error | ไทย: กำลัง retry request หลังเกิด transport error',
            {
              attempt,
              retryCount,
              url,
            },
          );
          continue;
        }
      }
    }

    throw lastError || normalizeError(new Error('Unknown request failure'));
  }

  get(path, options = {}) {
    return this.request('GET', path, options);
  }

  post(path, body, options = {}) {
    return this.request('POST', path, {
      ...options,
      body,
    });
  }

  put(path, body, options = {}) {
    return this.request('PUT', path, {
      ...options,
      body,
    });
  }

  patch(path, body, options = {}) {
    return this.request('PATCH', path, {
      ...options,
      body,
    });
  }

  delete(path, options = {}) {
    return this.request('DELETE', path, options);
  }

  getDiagnostics() {
    const diagnostics = {
      baseUrl: this.baseUrl,
      timeoutMs: this.timeoutMs,
      retryCount: this.retryCount,
      debug: this.debug,
      requestCount: this.requestCount,
      errorCount: this.errorCount,
      defaultHeaders: { ...this.defaultHeaders },
      lastRequestMeta: this.lastRequestMeta,
      lastResponseMeta: this.lastResponseMeta,
      explanation_en: 'Use this diagnostics snapshot to verify that apiClient is acting as one shared HTTP core layer for service facades and is centralizing request config, timeouts, retries, logging, and error normalization.',
      explanation_th: 'ใช้ diagnostics นี้ตรวจว่า apiClient ทำหน้าที่เป็น shared HTTP core layer เดียวสำหรับ service facades และรวมศูนย์การตั้งค่า request, timeout, retry, logging และการ normalize error แล้วหรือไม่',
    };

    // English: Logging removed — callers already receive the return value.
    //          Logging here caused a large object to be serialized on every call to
    //          getDiagnostics() (e.g. from pageDebugSnapshot reactive computed).
    // ไทย: ลบ log ออก — caller รับ return value อยู่แล้ว
    //      การ log ที่นี่ทำให้ serialize object ขนาดใหญ่ทุกครั้งที่เรียก getDiagnostics()
    return diagnostics;
  }
}

const apiClient = new ApiClient({
  baseUrl: '',
  defaultHeaders: {
    Accept: 'application/json',
  },
  timeoutMs: DEFAULT_TIMEOUT_MS,
  retryCount: DEFAULT_RETRY_COUNT,
  debug: DASHBOARD_DEBUG,
});

export function getApiClientDiagnostics() {
  return apiClient.getDiagnostics();
}

export { ApiClient };
export default apiClient;